import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  cn,
} from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
} from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import {
  DebouncedAction,
  formatTextDiffLimit,
  runTextDiffInWorker,
  textDiffLimit,
  ToolOperationLifecycle,
  type TextDiffResult,
  type TextDiffWorkerRequest,
} from '@/shared/text-diff'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

const ANALYTICS_CONTEXT = {
  app: 'devdeck',
  domain: 'devdeck.ru',
  route: '/text-diff',
  utility: 'text-diff',
  processingMode: 'local',
} satisfies ToolAnalyticsContext

const SAMPLE_ORIGINAL = `server:
  port: 8080
  logLevel: info
features:
  audit: false`

const SAMPLE_MODIFIED = `server:
  port: 8080
  logLevel: debug
features:
  audit: true
  metrics: true`

export function TextDiffPage() {
  const t = useT()
  const [original, setOriginal] = useState('')
  const [modified, setModified] = useState('')
  const [ignoreCase, setIgnoreCase] = useState(false)
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false)
  const [result, setResult] = useState<TextDiffResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hasCompared, setHasCompared] = useState(false)
  const [isComparing, setIsComparing] = useState(false)
  const [copied, setCopied] = useState(false)
  const requestId = useRef(0)
  const comparedOptions = useRef({ ignoreCase, ignoreWhitespace })
  const operationLifecycle = useRef(new ToolOperationLifecycle())
  const reactiveScheduler = useRef(new DebouncedAction())
  const activeComparison = useRef<TextDiffWorkerRequest | null>(null)

  const runComparison = useCallback(async (reactive = false) => {
    if (!reactive) {
      reactiveScheduler.current.cancel()
      comparedOptions.current = { ignoreCase, ignoreWhitespace }
    }
    activeComparison.current?.cancel()
    activeComparison.current = null
    operationLifecycle.current.cancel()
    if (!original && !modified) {
      setResult(null)
      setError(null)
      setHasCompared(false)
      return
    }

    const limit = textDiffLimit(original) ?? textDiffLimit(modified)
    if (limit) {
      requestId.current += 1
      setResult(null)
      setError(limit === 'bytes' ? t.textDiff.errors.bytes : t.textDiff.errors.lines)
      setIsComparing(false)
      if (!reactive) trackToolRejection(ANALYTICS_CONTEXT, 'size_limit')
      return
    }

    const currentRequest = ++requestId.current
    setIsComparing(true)
    setError(null)
    const startedAt = performance.now()
    if (!reactive) {
      operationLifecycle.current.start(
        currentRequest,
        startToolOperation(ANALYTICS_CONTEXT),
      )
    }

    try {
      const comparison = runTextDiffInWorker(original, modified, { ignoreCase, ignoreWhitespace })
      activeComparison.current = comparison
      const outcome = await comparison.promise
      if (currentRequest !== requestId.current) return
      if (outcome.type === 'timeout') {
        setResult(null)
        setError(t.textDiff.errors.timeout)
        operationLifecycle.current.error(currentRequest, 'timeout')
        return
      }
      if (outcome.type === 'processing_failed') {
        setResult(null)
        setError(t.textDiff.errors.failed)
        operationLifecycle.current.error(currentRequest, 'processing_failed')
        return
      }
      setResult(outcome.result)
      setHasCompared(true)
      comparedOptions.current = { ignoreCase, ignoreWhitespace }
      operationLifecycle.current.success(currentRequest, performance.now() - startedAt)
    } catch {
      if (currentRequest !== requestId.current) return
      setResult(null)
      setError(t.textDiff.errors.failed)
      operationLifecycle.current.error(currentRequest, 'processing_failed')
    } finally {
      if (currentRequest === requestId.current) {
        activeComparison.current = null
        setIsComparing(false)
      }
    }
  }, [ignoreCase, ignoreWhitespace, modified, original, t.textDiff.errors])

  useEffect(() => {
    if (!hasCompared) return
    if (
      comparedOptions.current.ignoreCase === ignoreCase
      && comparedOptions.current.ignoreWhitespace === ignoreWhitespace
    ) return
    comparedOptions.current = { ignoreCase, ignoreWhitespace }
    reactiveScheduler.current.schedule(() => void runComparison(true), 250)
    return () => reactiveScheduler.current.cancel()
  }, [hasCompared, ignoreCase, ignoreWhitespace, runComparison])

  useEffect(() => () => {
    requestId.current += 1
    reactiveScheduler.current.cancel()
    activeComparison.current?.cancel()
    activeComparison.current = null
    operationLifecycle.current.cancel()
  }, [])

  const invalidate = useCallback(() => {
    reactiveScheduler.current.cancel()
    requestId.current += 1
    activeComparison.current?.cancel()
    activeComparison.current = null
    operationLifecycle.current.cancel()
    setResult(null)
    setError(null)
    setHasCompared(false)
    setIsComparing(false)
    setCopied(false)
  }, [])

  const handleTextChange = useCallback((
    setter: (value: string) => void,
    nextValue: string,
  ) => {
    const limit = textDiffLimit(nextValue)
    if (limit) {
      invalidate()
      setError(limit === 'bytes' ? t.textDiff.errors.bytes : t.textDiff.errors.lines)
      trackToolRejection(ANALYTICS_CONTEXT, 'size_limit')
      return
    }
    setter(nextValue)
    invalidate()
  }, [invalidate, t.textDiff.errors])

  const handleCopy = useCallback(async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.unified)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1_500)
    } catch {
      setCopied(false)
    }
  }, [result])

  const { size, lines } = formatTextDiffLimit()
  const limitLabel = t.textDiff.limit
    .replace('{size}', size)
    .replace('{lines}', lines)

  return (
    <ToolPageShell toolKey="textDiff" width="wide">
      <div className="space-y-6">
        <Card className="overflow-hidden shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
            <div>
              <div className="text-sm font-medium">{t.textDiff.options}</div>
              <div className="mt-1 text-xs text-muted-foreground">{limitLabel}</div>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <label className="flex cursor-pointer items-center gap-2">
                <input type="checkbox" checked={ignoreCase} onChange={(event) => setIgnoreCase(event.target.checked)} />
                {t.textDiff.ignoreCase}
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input type="checkbox" checked={ignoreWhitespace} onChange={(event) => setIgnoreWhitespace(event.target.checked)} />
                {t.textDiff.ignoreWhitespace}
              </label>
            </div>
          </div>
          <div className="grid lg:grid-cols-2">
            {([
              { key: 'original', title: t.textDiff.original, value: original, setter: setOriginal, placeholder: t.textDiff.originalPlaceholder },
              { key: 'modified', title: t.textDiff.modified, value: modified, setter: setModified, placeholder: t.textDiff.modifiedPlaceholder },
            ] as const).map((pane, index) => (
              <label key={pane.key} className={cn('block', index === 1 && 'border-t lg:border-l lg:border-t-0')}>
                <span className="block border-b bg-muted/20 px-4 py-2 text-xs font-medium text-muted-foreground">{pane.title}</span>
                <textarea
                  value={pane.value}
                  onChange={(event) => {
                    handleTextChange(pane.setter, event.target.value)
                  }}
                  placeholder={pane.placeholder}
                  className="min-h-[280px] w-full resize-y bg-card p-4 font-mono text-sm leading-6 outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35"
                  spellCheck={false}
                />
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 border-t px-4 py-3">
            <Button onClick={() => void runComparison()} disabled={isComparing || (!original && !modified)}>
              {isComparing ? t.textDiff.comparing : t.textDiff.compare}
            </Button>
            <Button variant="outline" onClick={() => {
              const nextOriginal = modified
              setModified(original)
              setOriginal(nextOriginal)
              invalidate()
            }} className={!original && !modified ? 'hidden' : undefined}>{t.textDiff.swap}</Button>
            <Button variant="outline" onClick={() => {
              setOriginal(SAMPLE_ORIGINAL)
              setModified(SAMPLE_MODIFIED)
              invalidate()
            }}>{t.textDiff.sample}</Button>
            <Button variant="outline" onClick={() => {
              setOriginal('')
              setModified('')
              invalidate()
            }} className={!original && !modified ? 'hidden' : undefined}>{t.textDiff.clear}</Button>
          </div>
        </Card>

        {(error || result) && (
          <Card className="overflow-hidden shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
              <div>
                <h2 className="text-sm font-medium">{t.textDiff.result}</h2>
                {result && <p className="mt-1 text-xs text-muted-foreground" role="status" aria-live="polite">
                  {result.changed
                    ? t.textDiff.summary.replace('{added}', String(result.additions)).replace('{removed}', String(result.removals))
                    : t.textDiff.identical}
                </p>}
              </div>
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={!result} aria-live="polite">
                {copied ? t.textDiff.copied : t.textDiff.copy}
              </Button>
            </div>
            {error ? (
              <div className="bg-destructive/10 px-4 py-4 text-sm text-destructive" role="alert">{error}</div>
            ) : result && (
              <div className="max-h-[640px] overflow-auto font-mono text-xs sm:text-sm" aria-label={t.textDiff.resultAria}>
                {result.rows.map((row) => row.type === 'collapsed' ? (
                  <div key={row.id} className="border-y bg-muted/35 px-4 py-2 text-center text-muted-foreground">
                    {t.textDiff.collapsed.replace('{count}', String(row.collapsedCount ?? 0))}
                  </div>
                ) : row.type === 'marker' ? (
                  <div key={row.id} className="border-b bg-muted/25 px-4 py-1 italic text-muted-foreground">
                    {row.value}
                  </div>
                ) : (
                  <div key={row.id} className={cn(
                    'grid min-w-[520px] grid-cols-[52px_52px_24px_minmax(0,1fr)] border-b border-border/45',
                    row.type === 'added' && 'bg-emerald-50 text-emerald-950 dark:bg-emerald-950/35 dark:text-emerald-100',
                    row.type === 'removed' && 'bg-rose-50 text-rose-950 dark:bg-rose-950/35 dark:text-rose-100',
                  )}>
                    <span className="border-r px-2 py-1 text-right text-muted-foreground">{row.oldLine ?? ''}</span>
                    <span className="border-r px-2 py-1 text-right text-muted-foreground">{row.newLine ?? ''}</span>
                    <span className="px-2 py-1 select-none">{row.type === 'added' ? '+' : row.type === 'removed' ? '−' : ' '}</span>
                    <span className="whitespace-pre-wrap break-words py-1 pr-4">
                      {row.segments?.length ? row.segments.map((segment, index) => (
                        <mark key={`${row.id}-${index}`} className={cn(
                          'bg-transparent text-inherit',
                          segment.type === 'added' && 'bg-emerald-300/60 dark:bg-emerald-700/70',
                          segment.type === 'removed' && 'bg-rose-300/60 dark:bg-rose-700/70',
                        )}>{segment.value}</mark>
                      )) : row.value || ' '}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </ToolPageShell>
  )
}
